// src/App.tsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import Login from './pages/Login';
import ReleaseNotes from './pages/ReleaseNotes';
import Profile from './pages/Profile';
import LandingPage from './pages/LandingPage';
import MainHub from './pages/MainHub';
import LashingPlanBlog from './pages/LashingPlanBlog';
import Calculator from './pages/Calculator';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import SalesAgreement from './pages/SalesAgreement';
import LegalNotice from './pages/LegalNotice';
import AdminDashboard from './pages/AdminDashboard';
import { UserManualPage } from './pages/UserManualPage';
import { GuestSurveyUpload } from './pages/GuestSurveyUpload';
import InspectionPro from './pages/InspectionPro';
import Surveys from './pages/Surveys';
import { SurveyorLogin } from './pages/surveyor/SurveyorLogin';
import { SurveyorDashboard } from './pages/surveyor/SurveyorDashboard';
import { SurveyorReportEditor } from './pages/surveyor/SurveyorReportEditor';
import MarketingShowreel from './pages/MarketingShowreel';
import SteelCoilCalculator from './pages/SteelCoilCalculator';
import CookieConsent from './components/CookieConsent';
import { OfflineGuard } from './components/ui/OfflineGuard';
import { SWUpdateToast } from './components/ui/SWUpdateToast';
import { PWAInstallBanner } from './components/ui/PWAInstallBanner';
import { AppBottomNav } from './components/layout/AppBottomNav';
import { BetaAccessGuard } from './components/ui/BetaAccessGuard';
import { ProtectedRoute } from './components/ProtectedRoute';
import { 
  isAuthenticated, 
  logout, 
  getUser, 
  supabase,
  API_BASE_URL 
} from './auth';
import { appLogger } from './utils/logger';
import ScrollToTop from './components/ScrollToTop';

const BlogRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/lashing-plan-blog/${id}`} replace />;
};

const App: React.FC = () => {


  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isSurveyor, setIsSurveyor] = useState<boolean>(false);

  // Use global API_BASE_URL instead of local hardcoded default
  const API_URL = API_BASE_URL;

  useEffect(() => {
    let isMounted = true;

    // Fallback: If auth check takes more than 15 seconds, assume not logged in or show error
    const fallbackTimer = setTimeout(() => {
      if (isMounted && isLoggedIn === null) {
        appLogger.warn('Auth check taking too long, falling back to not logged in');
        setIsLoggedIn(false);
      }
    }, 15000);

    const checkAuth = async () => {
      try {
        appLogger.debug('Starting initial auth check');
        const isAuth = await isAuthenticated();
        if (isMounted) {
          clearTimeout(fallbackTimer);
          setIsLoggedIn(isAuth);
          if (isAuth) {
            const user = getUser();
            setUserEmail(user?.email || null);
            
            // Try fetching surveyor profile to check role
            const token = localStorage.getItem('jwt');
            if (token) {
              const cachedIsSurveyor = localStorage.getItem('isSurveyor') === 'true';
              if (cachedIsSurveyor) setIsSurveyor(true);

              try {
                const res = await fetch(`${API_URL}/api/surveyor/me`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                  const data = await res.json();
                  if (data) {
                    setIsSurveyor(true);
                    localStorage.setItem('isSurveyor', 'true');
                  } else {
                    setIsSurveyor(false);
                    localStorage.removeItem('isSurveyor');
                  }
                }
              } catch (e) {
                // Ignore connection errors, maintain cached surveyor status
              }
            }
          }
        }
      } catch (error) {
        appLogger.error('Error checking authentication', error as Error);
        if (isMounted) {
          clearTimeout(fallbackTimer);
          setIsLoggedIn(false);
        }
      }
    };
    checkAuth();
    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleLogin = async () => {
    setIsLoggedIn(true);
    const user = getUser();
    setUserEmail(user?.email || null);
    
    // Check role immediately after login
    const token = localStorage.getItem('jwt');
    if (token) {
      const cachedIsSurveyor = localStorage.getItem('isSurveyor') === 'true';
      if (cachedIsSurveyor) setIsSurveyor(true);
      
      try {
        const res = await fetch(`${API_URL}/api/surveyor/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setIsSurveyor(true);
            localStorage.setItem('isSurveyor', 'true');
          } else {
            setIsSurveyor(false);
            localStorage.removeItem('isSurveyor');
          }
        }
      } catch (e) {
        // Keep cached state if fetch fails
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsLoggedIn(false);
      setUserEmail(null);
      setIsSurveyor(false);
    } catch (error) {
      appLogger.error('Logout error', error as Error);
    }
  };

  // --- Multi-Session Realtime Guard ---
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const user = getUser();
    if (!user) return;
    const userId = user.sub || user.id;
    if (!userId) return;

    let localSessionId = localStorage.getItem('active_session_id');
    
    // Fallback provisioning if authenticated via memory but lacking a session stamp
    if (!localSessionId) {
       localSessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
       localStorage.setItem('active_session_id', localSessionId);
       supabase.from('user_profiles').upsert({ id: userId, email: user.email, active_session_id: localSessionId }, { onConflict: 'id' }).then();
    }

    const channel = supabase.channel(`public:user_profiles:${userId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${userId}`
      }, (payload: any) => {
        const activeSession = payload.new.active_session_id;
        if (activeSession && localSessionId && activeSession !== localSessionId) {
          appLogger.warn('Multi-session violation detected', { activeSession, localSessionId });
          handleLogout();
          alert('Hesabınıza başka bir cihazdan veya tarayıcıdan giriş yapıldı. Güvenlik sebebiyle oturumunuz sonlandırıldı.');
          window.location.href = '/login';
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isLoggedIn]);

  const SUPER_ADMIN_EMAIL = import.meta.env.VITE_SUPER_ADMIN_EMAIL || "superadm@lashingplan.com";

  // ... (inside App component)

  if (isLoggedIn === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-red-50 text-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p>Kimlik doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  // Helper to determine home route based on user role
  const getHomeRoute = () => {
    if (isLoggedIn && userEmail === SUPER_ADMIN_EMAIL) {
      return "/admin";
    }
    if (isLoggedIn && isSurveyor) {
      return "/surveyor/dashboard";
    }
    return isLoggedIn ? "/app" : "/";
  };

  const hostname = window.location.hostname;
  // Main hub is for lashingplan.com or www.lashingplan.com
  // app.lashingplan.com shows the specialized Calculator Landing Page
  const isMainHub = hostname === 'lashingplan.com' || hostname === 'www.lashingplan.com';
  // Fallback for local development: if not explicitly app.localhost, show MainHub
  const showMainHub = isMainHub || (hostname === 'localhost' && !window.location.search.includes('app=true'));

  return (
    <Router>
      <ScrollToTop />
      <OfflineGuard />
      <SWUpdateToast />
      <PWAInstallBanner />
      <AppBottomNav />
      <Routes>
        <Route path="/" element={showMainHub ? <MainHub /> : <LandingPage />} />
        <Route path="/release-notes" element={<ReleaseNotes />} />
        <Route path="/manual" element={<UserManualPage />} />
        <Route path="/lashing-plan-blog" element={<LashingPlanBlog />} />
        <Route path="/lashing-plan-blog/:id" element={<LashingPlanBlog />} />
        <Route path="/blog" element={<Navigate to="/lashing-plan-blog" />} />
        <Route path="/blog/:id" element={<BlogRedirect />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/sales-agreement" element={<SalesAgreement />} />
        <Route path="/legal-notice" element={<LegalNotice />} />
        <Route path="/submit-report/:guest_token" element={<GuestSurveyUpload />} />
        <Route path="/login" element={isLoggedIn ? <Navigate to={getHomeRoute()} /> : <Login onLoginSuccess={handleLogin} />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {userEmail === SUPER_ADMIN_EMAIL ? <Navigate to="/admin" /> : 
               isSurveyor ? <Navigate to="/surveyor/dashboard" /> : 
               <Calculator userEmail={userEmail} onLogout={handleLogout} />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {userEmail === SUPER_ADMIN_EMAIL ? <Navigate to="/admin" /> : 
               isSurveyor ? <Navigate to="/surveyor/dashboard" /> : 
               <Profile onLogout={handleLogout} />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/steel-coil"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {isSurveyor ? <Navigate to="/surveyor/dashboard" /> : (
                <BetaAccessGuard>
                  <SteelCoilCalculator />
                </BetaAccessGuard>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {userEmail === SUPER_ADMIN_EMAIL ? <Navigate to="/admin" /> : 
               isSurveyor ? <Navigate to="/surveyor/dashboard" /> : 
               <Surveys />}
            </ProtectedRoute>
          }
        />
        <Route path="/surveyor/login" element={isLoggedIn ? <Navigate to="/surveyor/dashboard" /> : <SurveyorLogin onLoginSuccess={handleLogin} />} />
        <Route
          path="/surveyor/dashboard"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {!isSurveyor ? <Navigate to="/app" /> : <SurveyorDashboard />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveyor/assignment/:id"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              {!isSurveyor ? <Navigate to="/app" /> : <SurveyorReportEditor />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/lashing-calculator" element={<LandingPage />} />
        <Route path="/calculator" element={<LandingPage />} />
        <Route path="/inspection-pro" element={<InspectionPro />} />
        <Route path="/promo" element={<MarketingShowreel />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <CookieConsent />
    </Router>
  );
};

const AppWithProviders = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <LanguageProvider>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </LanguageProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default AppWithProviders;
